// Hooks TanStack Query do módulo Workflow (M1 §8 "Data"): chaves começam por `['me','inbox',…]` ou pelo
// escopo (`['workflow', scopePath, …]`), para o clear de cache na troca de contexto (regra 6, 14.7) manter
// tudo coerente. Todas as chamadas passam por `useApiClient()` + `previewRequest` (pré-contrato, TODO GIY-023).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import { previewRequest } from '../../../lib/api/previewRequest';
import {
  type InboxItemsQuery,
  type WorkflowApprovalPolicy,
  type WorkflowEscalationRule,
  type WorkflowInboxCountersResponse,
  type WorkflowInboxItemsResponse,
  type WorkflowItemDetail,
  type WorkflowMetricsRow,
  type WorkflowTaskDetail,
  type WorkflowApprovalsResponse,
  type WorkflowExceptionsResponse,
  type WorkflowTasksResponse,
  type WorkItemKind,
  type WorkflowApprovalDetail,
  type WorkflowApprovalPolicyStep,
  type WorkflowExceptionDetail,
  type WorkflowInboxItem,
  WORKFLOW_PREVIEW_PATHS,
} from './contract.preview';

function asStr(val: unknown, fallback = ''): string {
  if (typeof val === 'string' && val.length > 0) return val;
  if (typeof val === 'number') return String(val);
  return fallback;
}

function normalizeWorkKind(rawKind: unknown, fallback: WorkItemKind = 'task'): WorkItemKind {
  if (typeof rawKind === 'string') {
    const lower = rawKind.toLowerCase();
    if (lower === 'task' || lower === 'approval' || lower === 'exception') {
      return lower;
    }
  }
  return fallback;
}

function normalizeInboxItem(raw: Record<string, unknown>, fallbackKind: WorkItemKind = 'task'): WorkflowInboxItem {
  const kind = normalizeWorkKind(raw['kind'], fallbackKind);
  const scopeRaw = (raw['scope'] as Record<string, unknown> | undefined) ?? {};
  const ownerRaw = (raw['owner'] as Record<string, unknown> | undefined) ?? {};
  const id = asStr(raw['id'], '');
  const typeCode = asStr(raw['typeCode'] ?? raw['taskTypeCode'] ?? raw['actType'] ?? raw['exceptionTypeCode'], 'WORKFLOW_ITEM');
  const status = (raw['status'] as WorkflowInboxItem['status']) ?? 'Open';
  const dueAt = (raw['dueAt'] ?? raw['slaDueAt'] ?? null) as string | null;
  const createdAt = asStr(raw['createdAt'] ?? raw['lastOccurredAt'], new Date().toISOString());

  return {
    kind,
    id,
    typeCode,
    subject: (raw['subject'] ?? raw['objectSummary'] ?? raw['detail'] ?? typeCode) as string | null,
    status,
    priority: (raw['priority'] as WorkflowInboxItem['priority']) ?? 'Normal',
    severity: (raw['severity'] as WorkflowInboxItem['severity']) ?? null,
    dueAt,
    overdue: Boolean(raw['overdue'] ?? false),
    createdAt,
    scope: {
      level: ((scopeRaw['level'] as WorkflowInboxItem['scope']['level']) ??
        (raw['studySiteId'] ? 'Study' : raw['tenantId'] ? 'Tenant' : 'Network')),
      tenantId: (scopeRaw['tenantId'] ?? raw['tenantId'] ?? null) as string | null,
      studySiteId: (scopeRaw['studySiteId'] ?? raw['studySiteId'] ?? null) as string | null,
    },
    owner: {
      kind: ((ownerRaw['kind'] ?? raw['ownerKind']) === 'User' ? 'User' : 'Role'),
      userId: (ownerRaw['userId'] ?? raw['ownerUserId'] ?? null) as string | null,
      roleCode: (ownerRaw['roleCode'] ?? raw['ownerRoleCode'] ?? 'NETWORK_ADMIN') as string | null,
    },
    assignedToMe: Boolean(raw['assignedToMe'] ?? false),
    claimable: Boolean(raw['claimable'] ?? (!raw['assigneeUserId'])),
    source: (raw['source'] as WorkflowInboxItem['source']) ?? null,
    consequence: (raw['consequence'] as WorkflowInboxItem['consequence']) ?? null,
    approval: (raw['approval'] as WorkflowInboxItem['approval']) ?? (kind === 'approval'
      ? {
          actType: asStr(raw['actType'], typeCode),
          objectType: asStr(raw['objectType'], 'WorkflowObject'),
          objectId: asStr(raw['objectId'], id),
          objectVersion: (raw['objectVersion'] as string | null) ?? '1',
          objectSummary: (raw['objectSummary'] as string | null) ?? null,
          objectContentHash: asStr(raw['objectContentHash'], 'sha256-verified'),
          step: Number(raw['currentStepOrder'] ?? 1),
          requiresSignature: Boolean(raw['requiresSignature'] ?? false),
          allowedMeanings: Array.isArray(raw['allowedMeanings']) ? (raw['allowedMeanings'] as never[]) : ['Approved'],
        }
      : null),
    allowedActions: Array.isArray(raw['allowedActions'])
      ? (raw['allowedActions'] as string[])
      : kind === 'task'
        ? ['claim', 'start', 'complete']
        : kind === 'approval'
          ? ['approve', 'reject', 'return']
          : ['assign', 'resolve', 'dismiss'],
  };
}

function extractItemsArray<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { items?: unknown }).items)) {
    return (payload as { items: T[] }).items;
  }
  return [];
}

function inboxQueryString(query: InboxItemsQuery): string {
  const params = new URLSearchParams();
  for (const kind of query.kind ?? []) {
    params.append('kind', kind);
  }
  if (query.tenantId) {
    params.set('tenantId', query.tenantId);
  }
  if (query.overdue) {
    params.set('overdue', 'true');
  }
  if (query.typeCode) {
    params.set('typeCode', query.typeCode);
  }
  if (query.cursor) {
    params.set('cursor', query.cursor);
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function useInboxItems(query: InboxItemsQuery) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['me', 'inbox', 'items', query],
    queryFn: async (): Promise<WorkflowInboxItemsResponse> => {
      const raw = await previewRequest<unknown>(
        client,
        'get',
        `${WORKFLOW_PREVIEW_PATHS.meInbox}${inboxQueryString(query)}`,
      );
      const items = extractItemsArray<Record<string, unknown>>(raw).map((item) =>
        normalizeInboxItem(item),
      );
      const nextCursor =
        raw && typeof raw === 'object' && 'nextCursor' in raw
          ? ((raw as { nextCursor?: string | null }).nextCursor ?? null)
          : null;
      return { items, nextCursor };
    },
    // A página mostra o próprio ProblemAlert (Parte 14.5): sem toast global duplicado.
    meta: { silent: true },
  });
}

export function useInboxCounters() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['me', 'inbox', 'counters'],
    queryFn: async (): Promise<WorkflowInboxCountersResponse> => {
      const raw = await previewRequest<Record<string, unknown>>(
        client,
        'get',
        WORKFLOW_PREVIEW_PATHS.meInboxCounters,
      );
      const byKindRaw = (raw?.['byKind'] as Record<string, unknown> | undefined) ?? {};
      return {
        total: Number(raw?.['total'] ?? 0),
        byKind: {
          task: Number(byKindRaw['task'] ?? 0),
          approval: Number(byKindRaw['approval'] ?? 0),
          exception: Number(byKindRaw['exception'] ?? 0),
        },
        overdue: Number(raw?.['overdue'] ?? 0),
        byScope: Array.isArray(raw?.['byScope'])
          ? (raw['byScope'] as WorkflowInboxCountersResponse['byScope'])
          : [],
      };
    },
    // REQ-WF-006: o painel/insígnia do Topbar reflete mudanças feitas em outra aba a cada minuto.
    refetchInterval: 60_000,
  });
}

export function useInboxItem(kind: WorkItemKind, id: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['me', 'inbox', 'item', kind, id],
    queryFn: () =>
      previewRequest<WorkflowItemDetail>(
        client,
        'get',
        WORKFLOW_PREVIEW_PATHS.meInboxItem(kind, id),
      ),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

function useInvalidateInbox() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'inbox'] });
    void queryClient.invalidateQueries({ queryKey: ['workflow'] });
  };
}

export interface TaskActionInput {
  readonly scopePath: string;
  readonly taskId: string;
  readonly action: 'claim' | 'start' | 'complete' | 'block' | 'cancel';
  readonly reason?: string;
}

export function useTaskAction() {
  const client = useApiClient();
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: (input: TaskActionInput) =>
      previewRequest<WorkflowTaskDetail>(
        client,
        'post',
        WORKFLOW_PREVIEW_PATHS.taskAction(input.scopePath, input.taskId, input.action),
        input.reason !== undefined ? { body: { reason: input.reason } } : {},
      ),
    onSuccess: invalidate,
  });
}

export interface ToggleChecklistItemInput {
  readonly scopePath: string;
  readonly taskId: string;
  readonly itemId: string;
  readonly isChecked: boolean;
}

export function useToggleChecklistItem() {
  const client = useApiClient();
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: (input: ToggleChecklistItemInput) =>
      previewRequest<WorkflowTaskDetail>(
        client,
        'put',
        WORKFLOW_PREVIEW_PATHS.taskChecklistItem(input.scopePath, input.taskId, input.itemId),
        { body: { isChecked: input.isChecked } },
      ),
    onSuccess: invalidate,
  });
}

export interface AddCommentInput {
  readonly scopePath: string;
  readonly taskId: string;
  readonly body: string;
}

export function useAddTaskComment() {
  const client = useApiClient();
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: (input: AddCommentInput) =>
      previewRequest<WorkflowTaskDetail>(
        client,
        'post',
        WORKFLOW_PREVIEW_PATHS.taskComments(input.scopePath, input.taskId),
        {
          body: { body: input.body },
        },
      ),
    onSuccess: invalidate,
  });
}

export interface DecideApprovalInput {
  readonly scopePath: string;
  readonly approvalId: string;
  readonly decision: 'Approved' | 'Rejected' | 'Returned';
  readonly meaning: string;
  readonly comment?: string;
  readonly expectedObjectContentHash: string;
}

export function useDecideApproval() {
  const client = useApiClient();
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: (input: DecideApprovalInput) =>
      previewRequest(
        client,
        'post',
        WORKFLOW_PREVIEW_PATHS.approvalDecide(input.scopePath, input.approvalId),
        {
          body: {
            decision: input.decision,
            meaning: input.meaning,
            comment: input.comment,
            expectedObjectContentHash: input.expectedObjectContentHash,
          },
        },
      ),
    onSuccess: invalidate,
    // ApprovalDetail mostra o próprio ProblemAlert (inclusive o desvio de step-up); sem toast duplicado.
    meta: { silent: true },
  });
}

export interface ExceptionActionInput {
  readonly scopePath: string;
  readonly exceptionId: string;
  readonly action: 'assign' | 'resolve' | 'dismiss';
  readonly reason: string;
}

export function useExceptionAction() {
  const client = useApiClient();
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: (input: ExceptionActionInput) =>
      previewRequest(
        client,
        'post',
        WORKFLOW_PREVIEW_PATHS.exceptionAction(input.scopePath, input.exceptionId, input.action),
        { body: { reason: input.reason } },
      ),
    onSuccess: invalidate,
  });
}

export function useTasksQueue(scopePath: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['workflow', scopePath, 'tasks'],
    queryFn: async (): Promise<WorkflowTasksResponse> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.tasks(scopePath));
      const items: WorkflowTaskDetail[] = extractItemsArray<Record<string, unknown>>(raw).map((r) => ({
        ...normalizeInboxItem(r, 'task'),
        kind: 'task',
        description: (r['description'] as string | null) ?? null,
        blockedReason: (r['blockedReason'] as string | null) ?? null,
        assigneeUserId: (r['assigneeUserId'] as string | null) ?? null,
        assigneeName: (r['assigneeName'] as string | null) ?? null,
        requiresEvidence: Boolean(r['requiresEvidence'] ?? false),
        evidencePresent: Boolean(r['evidencePresent'] ?? false),
        checklist: Array.isArray(r['checklist']) ? (r['checklist'] as WorkflowTaskDetail['checklist']) : [],
        comments: Array.isArray(r['comments']) ? (r['comments'] as WorkflowTaskDetail['comments']) : [],
        history: Array.isArray(r['history']) ? (r['history'] as WorkflowTaskDetail['history']) : [],
        etag: asStr(r['etag'], 'W/"1"'),
      }));
      return { items };
    },
    meta: { silent: true },
  });
}

export function useApprovalsQueue(scopePath: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['workflow', scopePath, 'approvals'],
    queryFn: async (): Promise<WorkflowApprovalsResponse> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.approvals(scopePath));
      const items: WorkflowApprovalDetail[] = extractItemsArray<Record<string, unknown>>(raw).map((r) => {
        const base = normalizeInboxItem(r, 'approval');
        return {
          ...base,
          kind: 'approval',
          approval: base.approval ?? {
            actType: asStr(r['actType'], base.typeCode),
            objectType: asStr(r['objectType'], 'WorkflowObject'),
            objectId: asStr(r['objectId'], base.id),
            objectVersion: (r['objectVersion'] as string | null) ?? '1',
            objectSummary: (r['objectSummary'] as string | null) ?? base.subject ?? null,
            objectContentHash: asStr(r['objectContentHash'], 'sha256-verified'),
            step: Number(r['currentStepOrder'] ?? 1),
            requiresSignature: Boolean(r['requiresSignature'] ?? false),
            allowedMeanings: ['Approved'],
          },
          decisions: Array.isArray(r['decisions']) ? (r['decisions'] as WorkflowApprovalDetail['decisions']) : [],
          etag: asStr(r['etag'], 'W/"1"'),
        };
      });
      return { items };
    },
    meta: { silent: true },
  });
}

export function useExceptionsQueue(scopePath: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['workflow', scopePath, 'exceptions'],
    queryFn: async (): Promise<WorkflowExceptionsResponse> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.exceptions(scopePath));
      const items: WorkflowExceptionDetail[] = extractItemsArray<Record<string, unknown>>(raw).map((r) => {
        const base = normalizeInboxItem(r, 'exception');
        return {
          ...base,
          kind: 'exception',
          detail: (r['detail'] as string | null) ?? base.subject ?? null,
          occurrenceCount: Number(r['occurrenceCount'] ?? 1),
          lastOccurredAt: asStr(r['lastOccurredAt'], base.createdAt),
          slaDueAt: asStr(r['slaDueAt'] ?? base.dueAt, base.createdAt),
          history: Array.isArray(r['history']) ? (r['history'] as WorkflowExceptionDetail['history']) : [],
          etag: asStr(r['etag'], 'W/"1"'),
        };
      });
      return { items };
    },
    meta: { silent: true },
  });
}

export function useWorkflowMetrics(scopePath: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['workflow', scopePath, 'metrics'],
    queryFn: async (): Promise<{ items: readonly WorkflowMetricsRow[] }> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.metrics(scopePath));
      const items: WorkflowMetricsRow[] = extractItemsArray<Record<string, unknown>>(raw).map((r) => {
        const agingRaw = (r['aging'] as Record<string, unknown> | undefined) ?? {};
        return {
          kind: normalizeWorkKind(r['kind'], 'task'),
          typeCode: asStr(r['typeCode'], 'GENERAL'),
          open: Number(r['open'] ?? 0),
          overdue: Number(r['overdue'] ?? 0),
          aging: {
            lt1d: Number(agingRaw['lt1d'] ?? 0),
            d1to3: Number(agingRaw['d1to3'] ?? 0),
            d3to7: Number(agingRaw['d3to7'] ?? 0),
            gt7d: Number(agingRaw['gt7d'] ?? 0),
          },
          resolvedInPeriod: Number(r['resolvedInPeriod'] ?? 0),
          meanResolutionHours: (r['meanResolutionHours'] as number | null) ?? null,
        };
      });
      return { items };
    },
    meta: { silent: true },
  });
}

export function useApprovalPolicies() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'workflow', 'approval-policies'],
    queryFn: async (): Promise<{ items: readonly WorkflowApprovalPolicy[] }> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.approvalPolicies);
      const items: WorkflowApprovalPolicy[] = extractItemsArray<Record<string, unknown>>(raw).map((r) => {
        let steps: WorkflowApprovalPolicyStep[] = Array.isArray(r['steps']) ? (r['steps'] as WorkflowApprovalPolicyStep[]) : [];
        if (steps.length === 0 && typeof r['stepsJson'] === 'string' && r['stepsJson'].trim().startsWith('[')) {
          try {
            steps = (JSON.parse(r['stepsJson']) as Record<string, unknown>[]).map((s, idx) => ({
              order: Number(s['order'] ?? s['Order'] ?? idx + 1),
              approverRoleCodes: Array.isArray(s['approverRoleCodes']) ? (s['approverRoleCodes'] as string[]) : [asStr(s['roleCode'], 'NETWORK_ADMIN')],
              approverScope: s['approverScope'] === 'Network' ? 'Network' : 'SameScope',
              requiredPermission: asStr(s['requiredPermission'], 'workflow.approve'),
              distinctFromPreviousSteps: Boolean(s['distinctFromPreviousSteps'] ?? true),
            }));
          } catch {
            steps = [];
          }
        }
        return {
          actType: asStr(r['actType'], ''),
          ownerModule: asStr(r['ownerModule'], 'Workflow'),
          steps,
          dueDays: (r['dueDays'] as number | null) ?? null,
          requiresSignature: Boolean(r['requiresSignature'] ?? false),
          allowedMeanings: Array.isArray(r['allowedMeanings']) ? (r['allowedMeanings'] as WorkflowApprovalPolicy['allowedMeanings']) : ['Approved'],
          requiresTwoKeys: Boolean(r['requiresTwoKeys'] ?? false),
          status: r['status'] === 'Inactive' ? 'Inactive' : 'Active',
        };
      });
      return { items };
    },
  });
}

export function useEscalationRules() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'workflow', 'escalation-rules'],
    queryFn: async (): Promise<{ items: readonly WorkflowEscalationRule[] }> => {
      const raw = await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.escalationRules);
      return { items: extractItemsArray<WorkflowEscalationRule>(raw) };
    },
  });
}
